import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import { useEffect, useRef, useState } from "react";
import { AspectRatio, Box, Text } from "@vinomofo/components";
import styled from "styled-components";
import type { Review } from "../../types";
import { debounce } from "../../utils/debounce";

export interface WineReviewProps {
  review: Review;
}

const Description = styled(Text)<{ isTruncated: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ isTruncated }) => (isTruncated ? 12 : 0)};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Button = styled.button`
  font-family: "Graphik", sans-serif;
  color: ${({ theme }) => theme.colors.primary};
  background: none;
  border: 0;
  outline: 0;
  padding: 0;
  cursor: pointer;
  margin-top: 10px;
  font-size: 20px;
  &:hover {
    text-decoration: underline;
  }
`;

export const WineReview = ({ review }: WineReviewProps): JSX.Element => {
  const { description, author } = review;
  const { profileImage, name, title, byline } = author;
  const descriptionRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(true);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const hasTruncation = (el) => {
      const { clientHeight, scrollHeight } = el;
      return clientHeight !== scrollHeight;
    };

    const checkButtonAvailability = () => {
      if (descriptionRef.current) {
        setShowButton(hasTruncation(descriptionRef.current));
      }
    };

    const debouncedCheck = debounce(checkButtonAvailability, 50);

    checkButtonAvailability();
    window.addEventListener("resize", debouncedCheck);

    return () => {
      window.removeEventListener("resize", debouncedCheck);
    };
  }, [descriptionRef]);
  const handleClick = () => setIsTruncated(!isTruncated);

  return (
    <Box display={["block", "block", "flex"]}>
      <Box
        textAlign="center"
        marginRight={[0, 0, "10%"]}
        marginBottom={["20px", "20px", 0]}
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Box
          borderRadius="50%"
          overflow="hidden"
          borderWidth={["3px", "3px", "6px"]}
          borderColor="brand"
          borderStyle="solid"
          width={["152px", "152px", "260px"]}
          mx="auto"
          marginBottom={["10px", "10px", "20px"]}
        >
          {!profileImage ? (
            <AspectRatio>
              <StaticImage
                src="../../images/review.jpg"
                alt="Review"
                formats={["auto", "webp", "avif"]}
                loading="eager"
                layout="fixed"
                objectFit="inherit"
              />
            </AspectRatio>
          ) : (
            <AspectRatio bg="white">
              <GatsbyImage image={profileImage} alt="Review" />
            </AspectRatio>
          )}
        </Box>
        <Text
          variant="md"
          fontWeight="500"
          fontFamily="heading"
          fontSize="18px"
          color="darkMofo"
        >
          {name}
        </Text>

        {title && title !== "null" && <Text variant="sm">{title}</Text>}
        {byline && byline !== "null" && <Text variant="sm">{byline}</Text>}
      </Box>
      <Box
        textAlign={["center", "center", "left"]}
        paddingBottom={[0, 0, "20px"]}
        paddingTop={[0, 0, "20px"]}
      >
        <Description
          fontSize={["16px", "18px", "20px"]}
          isTruncated={isTruncated}
          ref={descriptionRef}
        >
          {description}
        </Description>
        {showButton && (
          <Button onClick={handleClick} isTruncated={isTruncated}>
            Read {isTruncated ? "more" : "less"}
          </Button>
        )}
      </Box>
    </Box>
  );
};
